// src/components/Product/CarpetVisualizer.jsx

'use client'

import { useEffect, useRef, useState, useCallback } from 'react'; // Ensure React is imported if not globally available
import { useSearchParams, usePathname, useRouter } from 'next/navigation';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  Paper,
  Slider,
  Stack,
  Typography,
  styled
} from '@mui/material';
import {
  ZoomIn,
  ZoomOut,
  Upload,
  Rotate90DegreesCcw,
  Rotate90DegreesCw,
  Fullscreen,
  FullscreenExit
} from '@mui/icons-material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Grid from '@mui/material/GridLegacy';


// Styled component for the file input
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});


// --- Updated floorOptions with patternWidthCm ---
const floorOptions = [
  {
    name: { en: 'Concrete Floor 1', es: 'Suelo de Hormigón 1' },
    imagePath: '/assets/FloorSelectorAssets/Concrete_Floor_1.webp',
    patternWidthCm: 200, // Placeholder: Large pattern or seamless
  },
  {
    name: { en: 'Concrete Floor 2', es: 'Suelo de Hormigón 2' },
    imagePath: '/assets/FloorSelectorAssets/Concrete_Floor_2.webp',
    patternWidthCm: 200, // Placeholder
  },
  {
    name: { en: 'Polished Concrete 1', es: 'Hormigón Pulido 1' },
    imagePath: '/assets/FloorSelectorAssets/Concrete_Floor_Polished_Industrial_Style_1.webp',
    patternWidthCm: 200, // Placeholder
  },
  {
    name: { en: 'Polished Concrete 2', es: 'Hormigón Pulido 2' },
    imagePath: '/assets/FloorSelectorAssets/Concrete_Floor_Polished_Industrial_Style_2.webp',
    patternWidthCm: 200, // Placeholder
  },
  {
    name: { en: 'Oak Hardwood 1', es: 'Madera de Roble 1' },
    imagePath: '/assets/FloorSelectorAssets/Hardwood_Flooring_Oak_1.webp',
    patternWidthCm: 120, // Placeholder
  },
  {
    name: { en: 'Oak Hardwood 2', es: 'Madera de Roble 2' },
    imagePath: '/assets/FloorSelectorAssets/Hardwood_Flooring_Oak_2.webp',
    patternWidthCm: 120, // Placeholder
  },
  {
    name: { en: 'Herringbone Parquet', es: 'Parquet en Espiga' },
    imagePath: '/assets/FloorSelectorAssets/Herringbone_Parquet_2.webp',
    patternWidthCm: 70, // Placeholder
  },
  {
    name: { en: 'Gray Laminate 2', es: 'Laminado Gris 2' },
    imagePath: '/assets/FloorSelectorAssets/Laminate_Flooring_Modern_Gray_2.webp',
    patternWidthCm: 130, // Placeholder
  },
  {
    name: { en: 'White Marble 1', es: 'Mármol Blanco 1' },
    imagePath: '/assets/FloorSelectorAssets/Marble_Flooring_White_Carrara_1.webp',
    patternWidthCm: 150, // Placeholder
  },
  {
    name: { en: 'White Marble 2', es: 'Mármol Blanco 2' },
    imagePath: '/assets/FloorSelectorAssets/Marble_Flooring_White_Carrara_2.webp',
    patternWidthCm: 150, // Placeholder
  },
  {
    name: { en: 'White Marble 3', es: 'Mármol Blanco 3' },
    imagePath: '/assets/FloorSelectorAssets/Marble_Flooring_White_Carrara_3.webp',
    patternWidthCm: 150, // Placeholder
  },
  {
    name: { en: 'White Marble 4', es: 'Mármol Blanco 4' },
    imagePath: '/assets/FloorSelectorAssets/Marble_Flooring_White_Carrara_4.webp',
    patternWidthCm: 150, // Placeholder
  },
  {
    name: { en: 'Oak Wood 1', es: 'Madera de Roble 1' },
    imagePath: '/assets/FloorSelectorAssets/Oak_Wood_Flooring_1.webp',
    patternWidthCm: 120, // Placeholder
  },
  {
    name: { en: 'Oak Wood 2', es: 'Madera de Roble 2' },
    imagePath: '/assets/FloorSelectorAssets/Oak_Wood_Flooring_2.webp',
    patternWidthCm: 120, // Placeholder
  },
  {
    name: { en: 'Slate Stone 1', es: 'Piedra Pizarra 1' },
    imagePath: '/assets/FloorSelectorAssets/Slate_Stone_Tiles_1.webp',
    patternWidthCm: 90, // Placeholder
  },
  {
    name: { en: 'Slate Stone 2', es: 'Piedra Pizarra 2' },
    imagePath: '/assets/FloorSelectorAssets/Slate_Stone_Tiles_2.webp',
    patternWidthCm: 90, // Placeholder
  },
  {
    name: { en: 'Terracotta 1', es: 'Terracota 1' },
    imagePath: '/assets/FloorSelectorAssets/Terracotta_Tile_1.webp',
    patternWidthCm: 40, // Placeholder
  },
  {
    name: { en: 'Terracotta 2', es: 'Terracota 2' },
    imagePath: '/assets/FloorSelectorAssets/Terracotta_Tile_3.webp',
    patternWidthCm: 40, // Placeholder
  },
  {
    name: { en: 'Wood Look Vinyl 1', es: 'Vinilo Efecto Madera 1' },
    imagePath: '/assets/FloorSelectorAssets/Vinyl_Flooring_Modern_Wood_Look_1.webp',
    patternWidthCm: 110, // Placeholder
  },
  {
    name: { en: 'Wood Look Vinyl 2', es: 'Vinilo Efecto Madera 2' },
    imagePath: '/assets/FloorSelectorAssets/Vinyl_Flooring_Modern_Wood_Look_2.webp',
    patternWidthCm: 110, // Placeholder
  },
  {
    name: { en: 'Stone Effect Vinyl 1', es: 'Vinilo Efecto Piedra 1' },
    imagePath: '/assets/FloorSelectorAssets/Vinyl_Flooring_Stone_Effect_1.webp',
    patternWidthCm: 85, // Placeholder
  },
  {
    name: { en: 'Stone Effect Vinyl 2', es: 'Vinilo Efecto Piedra 2' },
    imagePath: '/assets/FloorSelectorAssets/Vinyl_Flooring_Stone_Effect_2.webp',
    patternWidthCm: 85, // Placeholder
  },
  {
    name: { en: 'Wool Carpet 1', es: 'Alfombra de Lana 1' },
    imagePath: '/assets/FloorSelectorAssets/Wool_Carpet_Loop_Pile_1.webp',
    patternWidthCm: 180, // Placeholder
  },
  {
    name: { en: 'Wool Carpet 2', es: 'Alfombra de Lana 2' },
    imagePath: '/assets/FloorSelectorAssets/Wool_Carpet_Loop_Pile_2.webp',
    patternWidthCm: 180, // Placeholder
  },
  // Add more floor options as needed
];


export default function CarpetVisualizer({ product, lang }) {
  // Return null if product is not available
  if (!product) return null;

  // Simple localization (added entries for new controls)
  const localization = {
    errors: {
      missingImage: {
        en: 'Missing rug image source. Cannot visualize the carpet.',
        es: 'Falta la imagen de la alfombra. No se puede visualizar la alfombra.'
      },
      missingDimensions: {
        en: 'Missing rug dimensions. Cannot visualize the carpet properly.',
        es: 'Faltan las dimensiones de la alfombra. No se puede visualizar la alfombra correctamente.'
      }
    },
    ui: {
      normalView: {
        en: 'Normal View',
        es: 'Vista Normal'
      },
      perspectiveView: {
        en: 'Perspective View',
        es: 'Vista en Perspectiva'
      },
      horizontalView: {
        en: 'Horizontal View',
        es: 'Vista Horizontal'
      },
      verticalView: {
        en: 'Vertical View',
        es: 'Vista Vertical'
      },
      uploadFloor: {
        en: 'Upload Your Floor:',
        es: 'Sube tu Suelo:'
      },
      floorZoom: { // Renamed from adjustScale / zoom
        en: 'Floor Pattern Size:',
        es: 'Tamaño Patrón Suelo:'
      },
      carpetZoom: { // New label for carpet zoom
        en: 'Carpet Size:',
        es: 'Tamaño Alfombra:'
      },
      saveImage: {
        en: 'Save Image',
        es: 'Guardar Imagen'
      },
      share: {
        en: 'Share',
        es: 'Compartir'
      }
    }
  };

  // Extract required data from product object
  const rugImageSrc = product?.mainImage?.webp?.url;
  const rugWidthCm = product?.measurements?.dimensions?.SI?.width;
  const rugLengthCm = product?.measurements?.dimensions?.SI?.length;
  const validRugWidth = typeof rugWidthCm === 'number' && rugWidthCm > 0;
  const validRugLength = typeof rugLengthCm === 'number' && rugLengthCm > 0;

  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const canvasRef = useRef(null);
  const [floorImage, setFloorImage] = useState(null);
  const [rugImage, setRugImage] = useState(null);
  const [perspective, setPerspective] = useState(searchParams.get('perspective') === 'true' || false);
  // New state for orientation, defaulting to horizontal
  const [orientation, setOrientation] = useState(searchParams.get('orientation') || 'horizontal');
  // --- Separate Zoom States ---
  const [carpetZoom, setCarpetZoom] = useState(parseFloat(searchParams.get('carpetZoom')) || 1);
  const [floorZoom, setFloorZoom] = useState(parseFloat(searchParams.get('floorZoom')) || 1); // User adjustment multiplier
  // Initialize fullscreen state from URL
  const [isFullScreen, setIsFullScreen] = useState(searchParams.get('fullscreen') === 'true' || false);

  // For selectedFloor, we store the index in the URL
  const floorIndexParam = searchParams.get('floorIndex');
  const initialFloorIndex = floorIndexParam !== null ? parseInt(floorIndexParam) : 0;
  const [selectedFloor, setSelectedFloor] = useState(floorOptions[initialFloorIndex >= 0 && initialFloorIndex < floorOptions.length ? initialFloorIndex : 0]);

  const [userFloorImage, setUserFloorImage] = useState(null); // Keep track if user uploaded custom floor
  const [error, setError] = useState(null);

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    // Ensure canvas and *both* images are loaded before attempting to draw
    if (!canvas || !rugImage || !floorImage) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // --- Check for required dimensions ---
    if (!validRugWidth || !validRugLength) {
        console.error("Rug dimensions are missing or invalid. Cannot scale proportionally.");
        return
        // Optionally draw a placeholder or just return to prevent errors
        // ctx.setTransform(1, 0, 0, 1, 0, 0);
        // ctx.clearRect(0, 0, canvas.width, canvas.height);
        // You could draw an error message on the canvas here if desired
    }

    // --- 1. Clear Canvas ---
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    try {
      // --- Calculate Base Rug Display Dimensions (before carpetZoom) ---
      // INCREASED BASE SIZE HERE - changed from 0.8 to 0.9 for larger initial view
      const rugAspectRatio = rugWidthCm / rugLengthCm;
      const maxBaseRugWidth = canvas.width * 0.9;  // Increased from 0.8 to 0.9 (90% of canvas width)
      const maxBaseRugHeight = canvas.height * 0.9; // Increased from 0.8 to 0.9 (90% of canvas height)
      let baseDisplayRugWidth = maxBaseRugWidth;
      let baseDisplayRugHeight = baseDisplayRugWidth / rugAspectRatio;
      if (baseDisplayRugHeight > maxBaseRugHeight) {
        baseDisplayRugHeight = maxBaseRugHeight;
        baseDisplayRugWidth = baseDisplayRugHeight * rugAspectRatio;
      }

      // Ensure calculated base dimensions are valid
      if (baseDisplayRugWidth <= 0 || baseDisplayRugHeight <= 0) {
           console.error("Calculated base rug display dimensions are invalid:", baseDisplayRugWidth, baseDisplayRugHeight);
           return; // Stop drawing if dimensions are bad
      }

      // --- 2. Draw Floor Pattern ---
      ctx.save(); // Save clean state before drawing floor
      const patternCanvas = document.createElement('canvas');
      const pCtx = patternCanvas.getContext('2d');
      if (!pCtx) {
          console.error("Failed to get pattern canvas context.");
          ctx.restore();
          return;
      }

      // --- Calculate Proportional Floor Pattern Size ---
      // Use selected floor's pattern width, or a default if custom/missing
      const floorPatternWidthCm = selectedFloor?.patternWidthCm || 100; // Default to 100cm if missing
      // Calculate how wide one pattern tile *should* be on canvas relative to the *base* rug size
      const basePatternDisplayWidth = (baseDisplayRugWidth * floorPatternWidthCm) / rugWidthCm;
      // Calculate the aspect ratio of the floor pattern image itself
      const floorImageAspectRatio = floorImage.naturalHeight > 0 ? floorImage.naturalHeight / floorImage.naturalWidth : 1; // Avoid div by zero

      // Apply the user's floorZoom adjustment *on top* of the calculated base size
      const finalPatternW = Math.max(1, basePatternDisplayWidth * floorZoom);
      const finalPatternH = Math.max(1, finalPatternW * floorImageAspectRatio); // Maintain aspect ratio

      // Set pattern canvas dimensions and draw the scaled floor image onto it
      patternCanvas.width = finalPatternW;
      patternCanvas.height = finalPatternH;
      pCtx.drawImage(floorImage, 0, 0, finalPatternW, finalPatternH);

      // Create the pattern and fill the background
      const pattern = ctx.createPattern(patternCanvas, 'repeat');
      if (!pattern) {
          console.error("Failed to create floor pattern.");
          ctx.restore();
          return;
      };

      ctx.fillStyle = pattern;
      ctx.fillRect(0, 0, canvas.width, canvas.height); // Fill the entire canvas viewport
      ctx.restore(); // Restore clean state after floor drawing

      // --- 3. Draw Carpet (With optional perspective and carpet zoom) ---
      ctx.save(); // Save the clean state before transforming for the carpet

      // Center the transformation origin
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      ctx.translate(centerX, centerY);

      // Apply perspective transformation *if* enabled
      if (perspective) {
          ctx.transform(1, -0.2, 0, 0.95, 0, 0);
      }

      // FIX: Fixed rotation logic - now correctly implements the behavior where:
      // - "vertical" orientation shows the carpet vertically (as if standing up)
      // - "horizontal" orientation shows the carpet horizontally (lying flat)
      if (orientation === 'vertical') {
        // No rotation needed - vertical orientation keeps the carpet upright
      } else {
        // Horizontal orientation needs to rotate the carpet 90 degrees
        ctx.rotate((90 * Math.PI) / 180);
      }

      // Apply carpetZoom to the *base* dimensions calculated earlier
      const zoomedRugWidth = baseDisplayRugWidth * carpetZoom;
      const zoomedRugHeight = baseDisplayRugHeight * carpetZoom;

      // Draw the rug image centered, with calculated and zoomed dimensions
      ctx.drawImage(
        rugImage,
        -zoomedRugWidth / 2,  // Draw relative to the translated center
        -zoomedRugHeight / 2,
        zoomedRugWidth,
        zoomedRugHeight
      );

      ctx.restore(); // Restore the state saved before carpet transformations
    } catch (drawError) {
        console.error("Error during canvas drawing:", drawError);
        // Attempt to restore context state if an error occurred mid-draw
        try { ctx.restore(); } catch(e) {}
    }
  };

  // --- Effects ---

  // TEST THIS USEEFFECT AND DELETE IT COMPLETELY IF BAD
  // Handle pinch-to-zoom on mobile devices
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let initialDistance = 0;
    let initialZoom = 1;
    let touchStarted = false;

    const getTouchDistance = (touches) => {
      if (touches.length < 2) return 0;
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const handleTouchStart = (e) => {
      if (e.touches.length === 2) {
        touchStarted = true;
        initialDistance = getTouchDistance(e.touches);
        initialZoom = carpetZoom;
        e.preventDefault(); // Prevent default to avoid page zooming
      }
    };

    const handleTouchMove = (e) => {
      if (!touchStarted || e.touches.length !== 2) return;

      const currentDistance = getTouchDistance(e.touches);
      if (initialDistance === 0 || currentDistance === 0) return;

      // Calculate zoom ratio based on pinch distance change
      const zoomDelta = currentDistance / initialDistance;
      const newZoom = Math.min(2, Math.max(0.1, initialZoom * zoomDelta));

      setCarpetZoom(newZoom);
      e.preventDefault(); // Prevent default to avoid page zooming
    };

    const handleTouchEnd = () => {
      touchStarted = false;
      initialDistance = 0;
    };

    // Add touch event listeners
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd);
    canvas.addEventListener('touchcancel', handleTouchEnd);

    // Clean up event listeners on unmount
    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
      canvas.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [carpetZoom, setCarpetZoom]);

  // Validate product data
  useEffect(() => {
    if (!rugImageSrc) {
      setError(localization.errors.missingImage[lang] || localization.errors.missingImage.en);
    } else if (!rugWidthCm || !rugLengthCm) {
      setError(localization.errors.missingDimensions[lang] || localization.errors.missingDimensions.en);
    } else {
      setError(null);
    }
  }, [rugImageSrc, rugWidthCm, rugLengthCm, lang, localization.errors]);

  // Handle window resize
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleResize = () => {
        const rect = canvas.parentElement?.getBoundingClientRect(); // Safer access
        if (rect) {
          // Directly set canvas buffer size to match CSS size
          canvas.width = rect.width;
          canvas.height = rect.height;
          // Request redraw AFTER state update and canvas resize
          requestAnimationFrame(drawCanvas);
        }
    };

    // Create a ResizeObserver for more reliable detection of element resize
    const resizeObserver = new ResizeObserver(handleResize);
    if (canvas.parentElement) {
       resizeObserver.observe(canvas.parentElement); // Observe the container
    }

    handleResize(); // Initial setup call

    return () => {
        resizeObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rugImage, floorImage]); // Redraw depends on images being loaded (and drawCanvas dependencies)

  // Load rug image
  useEffect(() => {
    if (!rugImageSrc) {
        setRugImage(null); // Ensure rug image is null if no src
        return;
    }
    console.log("Attempting to load rug image:", rugImageSrc);
    let isMounted = true; // Track mount status
    const img = new Image();
    // img.crossOrigin = "Anonymous"; // Keep commented unless CORS issues arise AND server supports it
    img.src = rugImageSrc;
    img.onload = () => {
        if (isMounted) {
            console.log('Rug image loaded successfully:', rugImageSrc);
            setRugImage(img);
            setError(prevError => prevError === localization.errors.missingImage[lang] || prevError === localization.errors.missingImage.en ? null : prevError); // Clear only missing image error
        }
    };
    img.onerror = (e) => {
        if (isMounted) {
          console.error('Error loading rug image:', e, 'Image src:', img.src);
          setError('Failed to load rug image.');
          setRugImage(null); // Clear image on error
        }
    };
    return () => { isMounted = false; }; // Cleanup on unmount or src change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rugImageSrc]); // Dependency only on source URL

  // Load selected floor image
  useEffect(() => {
    const floorSrc = userFloorImage || selectedFloor?.imagePath; // Use optional chaining
    if (!floorSrc) {
        setFloorImage(null); // Ensure floor image is null if no src
        return;
    }
    console.log("Attempting to load floor image:", floorSrc);
    let isMounted = true; // Track mount status
    const img = new Image();
    // img.crossOrigin = "Anonymous"; // Keep commented unless CORS issues arise AND server supports it
    img.src = floorSrc;
    img.onload = () => {
        if (isMounted) {
            console.log('Floor image loaded successfully:', floorSrc);
            setFloorImage(img);
             setError(prevError => prevError === 'Failed to load floor image.' ? null : prevError); // Clear only floor image load error
        }
    };
    img.onerror = (e) => {
      if (isMounted) {
        console.error('Error loading floor image:', e, 'Image src:', img.src);
        setError('Failed to load floor image.');
        setFloorImage(null); // Clear image on error
      }
    };
     return () => { isMounted = false; }; // Cleanup on unmount or src change
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFloor, userFloorImage]); // Dependency on selected floor or uploaded image

  // Redraw canvas on relevant state changes
  useEffect(() => {
    // Directly call drawCanvas when dependencies change
    // Ensure images are loaded before drawing (checked inside drawCanvas)
    console.log("Redrawing canvas due to state change...");
    requestAnimationFrame(drawCanvas); // Use rAF for smoother updates

    // Ensure all dependencies of drawCanvas are listed here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rugImage, floorImage, perspective, carpetZoom, floorZoom, orientation, selectedFloor, rugWidthCm, rugLengthCm]); // Added orientation

  // --- URL Parameter Syncing ---
  // Use useCallback to memoize the function and prevent unnecessary re-renders
  const updateUrlParams = useCallback((newParams) => {
    // Save current scroll position
    const scrollPosition = window.scrollY;

    const params = new URLSearchParams(searchParams);
    Object.entries(newParams).forEach(([key, value]) => {
        // More robust check for default values to remove from URL
        const isDefault =
            value === null ||
            value === undefined ||
            (key === 'perspective' && value === false) ||
            (key === 'carpetZoom' && value === 1) ||
            (key === 'floorZoom' && value === 1) ||
            (key === 'floorIndex' && value === 0) ||
            (key === 'orientation' && value === 'horizontal') || // Default orientation is horizontal
            (key === 'fullscreen' && value === false); // Default fullscreen is false

        if (isDefault && params.has(key)) { // Only delete if it exists
            params.delete(key);
        } else if (!isDefault) {
            // FIX: Format numeric values appropriately
            let formattedValue;
            if (key === 'floorIndex') {
                // Floor index is an integer, don't add decimal places
                formattedValue = value.toString();
            } else if (typeof value === 'number') {
                // Format other numbers to 2 decimal places
                formattedValue = value.toFixed(2);
            } else {
                formattedValue = value.toString();
            }
            params.set(key, formattedValue);
        }
    });

    // Use replace to avoid excessive history entries
    // Only push if params actually changed
    const currentQueryString = searchParams.toString();
    const newQueryString = params.toString();
    if (currentQueryString !== newQueryString) {
        // Use router.replace with { scroll: false } option
        router.replace(`${pathname}?${newQueryString}`, { 
            scroll: false,
        });

        // Restore scroll position after URL update
        // Use setTimeout to ensure this runs after the router update
        setTimeout(() => {
            window.scrollTo({
                top: scrollPosition,
                behavior: 'auto' // Use 'auto' for immediate scrolling without animation
            });
        }, 0);
    }
  }, [searchParams, pathname, router]);

  // Use separate effects for cleaner state-to-URL updates
  useEffect(() => {
    updateUrlParams({ perspective });
  }, [perspective, updateUrlParams]);

  useEffect(() => {
    updateUrlParams({ carpetZoom });
  }, [carpetZoom, updateUrlParams]);

  useEffect(() => {
    updateUrlParams({ floorZoom });
  }, [floorZoom, updateUrlParams]);

  useEffect(() => {
    updateUrlParams({ orientation });
  }, [orientation, updateUrlParams]);

  useEffect(() => {
    updateUrlParams({ fullscreen: isFullScreen });

    // Add keyboard event listener to exit full-screen mode with Escape key
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullScreen) {
        setIsFullScreen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // Clean up event listener
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullScreen, updateUrlParams]);

  useEffect(() => {
    // Only update floorIndex if a predefined floor is selected (and it's not the default 0)
    if (selectedFloor && !userFloorImage) {
        const currentFloorIndex = floorOptions.findIndex(floor => floor === selectedFloor);
        updateUrlParams({ floorIndex: currentFloorIndex >= 0 ? currentFloorIndex : null }); // Ensure valid index
    } else {
        // Remove floorIndex if custom image is used or none selected
        updateUrlParams({ floorIndex: null });
    }
  }, [selectedFloor, userFloorImage, updateUrlParams, floorOptions]);


  // --- Event Handlers ---
  const handleFloorUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
            setUserFloorImage(event.target.result); // Store the base64 URL
            setSelectedFloor(null); // Deselect predefined floor
            // Reset floor zoom to default when changing to custom? Optional.
            // setFloorZoom(1);
            setError(null);
        };
        reader.onerror = () => {
             setError("Failed to read the uploaded file.");
             setUserFloorImage(null); // Clear on error
        }
        reader.readAsDataURL(file);
    } else if (file) {
         setError("Please upload a valid image file.");
    }
    // Reset the input value allows uploading the same file again if needed
    e.target.value = null;
  };

  // const saveCanvasImage = () => {
  //   const canvas = canvasRef.current;
  //   // Also check if rug/floor images are actually loaded before saving
  //   if (!canvas || !rugImage || !floorImage) {
  //       alert("Visualizer not ready to save (images might still be loading or failed).");
  //       return;
  //   }
  //   const link = document.createElement('a');
  //   link.download = 'carpet-visualization.webp';
  //   try {
  //       link.href = canvas.toDataURL('image/png'); // Use simple toDataURL
  //       link.click();
  //   } catch (err) {
  //       console.error("Error saving canvas image:", err);
  //       alert("Could not save image. This might be due to using images from another website (CORS issue) or canvas errors.");
  //   }
  // };
  //
  // const shareVisualization = () => {
  //   const canvas = canvasRef.current;
  //    if (!canvas || !rugImage || !floorImage) {
  //       alert("Visualizer not ready to share (images might still be loading or failed).");
  //       return;
  //   }
  //   if (!navigator.share) {
  //       alert('Web Share API is not supported in your browser.');
  //       return;
  //   }
  //
  //   canvas.toBlob((blob) => {
  //       if (!blob) {
  //            console.error("Failed to create blob from canvas.");
  //            alert("Could not generate image for sharing.");
  //            return;
  //       }
  //     const file = new File([blob], 'carpet-visualization.webp', { type: 'image/png' });
  //
  //     // Check if browser *can* share these files
  //     if (navigator.canShare && navigator.canShare({ files: [file] })) {
  //       navigator.share({
  //           title: 'Carpet Visualization',
  //           text: 'Check out this carpet visualization!',
  //           files: [file],
  //       })
  //       .catch((error) => {
  //           // Avoid logging AbortError if user cancels share dialog
  //           if (error.name !== 'AbortError') {
  //               console.error('Sharing failed:', error);
  //               alert(`Sharing failed: ${error.message}`);
  //           }
  //       });
  //     } else {
  //         // Fallback or message if file sharing specifically isn't supported
  //         // Try sharing URL only as fallback?
  //         console.warn("File sharing not supported, attempting URL share...");
  //         const shareUrl = window.location.href; // Share the current URL with parameters
  //          if (navigator.canShare && navigator.canShare({ url: shareUrl })) {
  //              navigator.share({
  //                  title: 'Carpet Visualization',
  //                  text: 'Check out this carpet visualization setup!',
  //                  url: shareUrl
  //              }).catch((error) => {
  //                 if (error.name !== 'AbortError') {
  //                    console.error('URL Sharing failed:', error);
  //                    alert(`Sharing failed: ${error.message}`);
  //                 }
  //              });
  //          } else {
  //               alert('Your browser does not support sharing files or URLs via the Share API.');
  //          }
  //     }
  //   }, 'image/png'); // Specify mime type for blob
  // };

  // Note: Removed problematic handleInteraction wrapper that was interfering with button functionality
  // The mobile scroll issue will need a different solution

  // Function to safely get localized string
  const getLoc = (key, subkey) => {
      return localization[key]?.[subkey]?.[lang] || localization[key]?.[subkey]?.en || `[${key}.${subkey}]`;
  }

  // --- Render ---
  return (
    <Box sx={{
      maxWidth: isFullScreen ? '120rem' : '70rem',
      width: '100%',
      mx: 'auto',
      p: { xs: 1, sm: 2 },
      bgcolor: 'background.paper',
      borderRadius: isFullScreen ? 0 : 2,
      boxShadow: isFullScreen ? 0 : 3,
      overflow: 'hidden', // Prevent controls overflowing card
      position: isFullScreen ? 'fixed' : 'relative',
      top: isFullScreen ? 0 : 'auto',
      left: isFullScreen ? 0 : 'auto',
      right: isFullScreen ? 0 : 'auto',
      bottom: isFullScreen ? 0 : 'auto',
      zIndex: isFullScreen ? 1300 : 'auto',
      height: isFullScreen ? '100vh' : 'auto',
      overflowY: isFullScreen ? 'auto' : 'visible'
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h2" gutterBottom sx={{ textAlign: 'center', mb: 0 }}>
          Interactive Carpet Visualizer
        </Typography>
        <IconButton 
          onClick={() => setIsFullScreen(!isFullScreen)} 
          aria-label={isFullScreen ? "Exit full screen" : "Enter full screen"}
          size="large"
        >
          {isFullScreen ? <FullscreenExit /> : <Fullscreen />}
        </IconButton>
      </Box>
      <Card variant="outlined" sx={{ height: isFullScreen ? 'calc(100vh - 80px)' : 'auto' }}>
        <CardContent sx={{ 
          p: { xs: 1, sm: 2 }, 
          '&:last-child': { pb: { xs: 2, sm: 3 } },
          height: isFullScreen ? '100%' : 'auto',
          display: 'flex',
          flexDirection: 'column'
        }}> {/* Ensure padding at bottom */}
          {/* Error message */}
          {error && (
            <Paper
              elevation={0}
              sx={{
                bgcolor: 'error.light',
                color: 'error.contrastText',
                p: 1.5,
                mb: 2,
                borderRadius: 1
              }}
            >
              <Typography variant="body2">{error}</Typography>
            </Paper>
          )}

          {/* Canvas container */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: isFullScreen ? 'auto' : '16 / 10', // Maintain aspect ratio only when not in full screen
              maxHeight: isFullScreen ? { xs: 'calc(50vh)', md: 'calc(70vh)' } : { xs: '350px', sm: '450px', md: '550px' },
              height: isFullScreen ? { xs: 'calc(50vh)', md: 'calc(70vh)' } : 'auto',
              bgcolor: 'grey.200',
              overflow: 'hidden',
              mb: 2,
              border: '1px solid', // Add a subtle border for visual bounds
              borderColor: 'divider'
            }}
          >
            <canvas
              ref={canvasRef}
              aria-label="Carpet visualization"
              style={{
                display: 'block',
                width: '100%',
                height: '100%',
              }}
            >
              Your browser does not support the HTML canvas tag.
            </canvas>
             {/* Loading Indicator (Optional) */}
             {(!rugImage || !floorImage) && !error && (
                 <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: 'text.secondary' }}>
                     <Typography variant="caption">Loading visualization...</Typography>
                     {/* You could add a CircularProgress spinner here */}
                 </Box>
             )}
          </Box>

          {/* Controls */}
          <Box sx={{ 
            px: { xs: 0, sm: 1 },
            flex: isFullScreen ? 1 : 'none',
            overflow: isFullScreen ? 'auto' : 'visible',
            display: 'flex',
            flexDirection: 'column'
          }}>
             {/* Orientation toggle */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" component={'h3'} gutterBottom>Carpet Orientation</Typography>
              <ToggleButtonGroup
                value={orientation}
                exclusive
                onChange={(e, newValue) => {
                  // Prevent deselecting all options
                  if (newValue !== null) {
                    // When switching to vertical view, recalculate carpet size to fit within canvas
                    if (newValue === 'vertical' && orientation !== 'vertical') {
                      // Set a smaller carpet zoom value for vertical view
                      // This allows the carpet to be smaller in height than the canvas height
                      // User can still increase it later if desired
                      setCarpetZoom(1); // Adjust this value as needed for best appearance
                    }
                    setOrientation(newValue);
                  }
                }}
                aria-label="carpet orientation"
                size="small"
                fullWidth // Make buttons take full width for better layout
              >
                <ToggleButton value="horizontal" aria-label="horizontal view">
                  <Rotate90DegreesCw sx={{ mr: 1 }} />
                  {getLoc('ui', 'horizontalView')}
                </ToggleButton>
                <ToggleButton value="vertical" aria-label="vertical view">
                  <Rotate90DegreesCcw sx={{ mr: 1 }} />
                  {getLoc('ui', 'verticalView')}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Grid container spacing={2} alignItems="center">
              {/* Floor Zoom Control */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" component={'h3'} gutterBottom>
                  {getLoc('ui', 'floorZoom')}
                </Typography>
                <Stack spacing={2} direction="row" sx={{ mb: 1 }} alignItems="center">
                  <IconButton
                    onClick={() => setFloorZoom(Math.max(0.2, floorZoom - 0.1))}
                    size="small"
                    aria-label="decrease floor pattern size"
                  >
                    <ZoomOut />
                  </IconButton>
                  <Slider
                    aria-label={getLoc('ui', 'floorZoom')}
                    value={floorZoom}
                    min={0.2} // Min zoom level
                    max={3} // Max zoom level
                    step={0.1}
                    onChange={(e, newValue) => setFloorZoom(newValue)}
                  />
                  <IconButton
                    onClick={() => setFloorZoom(Math.min(3, floorZoom + 0.1))}
                    size="small"
                    aria-label="increase floor pattern size"
                  >
                    <ZoomIn />
                  </IconButton>
                </Stack>
              </Grid>

               {/* Carpet Zoom Control */}
               <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" component={'h3'} gutterBottom>
                  {getLoc('ui', 'carpetZoom')}
                </Typography>
                <Stack spacing={2} direction="row" sx={{ mb: 1 }} alignItems="center">
                  <IconButton
                    onClick={() => setCarpetZoom(Math.max(0.1, carpetZoom - 0.05))}
                    size="small"
                    aria-label="decrease carpet size"
                  >
                    <ZoomOut />
                  </IconButton>
                  <Slider
                    aria-label={getLoc('ui', 'carpetZoom')}
                    value={carpetZoom}
                    min={0.1} // Min zoom level
                    max={2}   // Max zoom level
                    step={0.05}
                    onChange={(e, newValue) => setCarpetZoom(newValue)}
                  />
                  <IconButton
                    onClick={() => setCarpetZoom(Math.min(2, carpetZoom + 0.05))}
                    size="small"
                    aria-label="increase carpet size"
                  >
                    <ZoomIn />
                  </IconButton>
                </Stack>
              </Grid>
            </Grid>

            {/* Perspective Toggle */}
            {/* <Box sx={{ mb: 2, mt: 2 }}> /!* Added margin top *!/*/}
            {/*  <Typography variant="subtitle1" component={'h3'} gutterBottom>View Style</Typography>*/}
            {/*  <ToggleButtonGroup*/}
            {/*      value={perspective ? 'perspective' : 'normal'}*/}
            {/*      exclusive*/}
            {/*      onChange={(e, newValue) => {*/}
            {/*        if (newValue !== null) {*/}
            {/*          setPerspective(newValue === 'perspective');*/}
            {/*        }*/}
            {/*      }}*/}
            {/*      aria-label="view style"*/}
            {/*      size="small"*/}
            {/*      fullWidth // Make buttons take full width*/}
            {/*  >*/}
            {/*      <ToggleButton value="normal" aria-label="normal view">*/}
            {/*        {getLoc('ui', 'normalView')}*/}
            {/*      </ToggleButton>*/}
            {/*      <ToggleButton value="perspective" aria-label="perspective view">*/}
            {/*        {getLoc('ui', 'perspectiveView')}*/}
            {/*      </ToggleButton>*/}
            {/*  </ToggleButtonGroup>*/}
            {/*</Box>*/}

            <Divider sx={{ my: 2 }} />

            {/* Floor Selection */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" component={'h3'} gutterBottom>Select Floor</Typography>
              <Grid container spacing={1}>
                {floorOptions.map((floor, index) => (
                  <Grid item xs={3} sm={isFullScreen ? 1 : 2} md={isFullScreen ? 1 : 2} key={index}>
                    <Button
                      variant={selectedFloor === floor ? 'contained' : 'outlined'}
                      onClick={() => {
                        setSelectedFloor(floor);
                        setUserFloorImage(null); // Clear user image when selecting predefined
                        // Optionally reset floor zoom
                        // setFloorZoom(1);
                      }}
                      sx={{
                        width: '100%',
                        height: '60px', // Fixed height for consistency
                        p: 0,
                        overflow: 'hidden',
                        position: 'relative',
                        border: selectedFloor === floor ? '2px solid' : '1px solid',
                        borderColor: selectedFloor === floor ? 'primary.main' : 'divider',
                        '&:hover': {
                           opacity: 0.8
                        }
                      }}
                      aria-label={`Select floor ${floor.name[lang] || floor.name.en}`}
                    >
                      <img
                        src={floor.imagePath}
                        alt={floor.name[lang] || floor.name.en}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        loading="lazy" // Lazy load floor previews
                      />
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Custom Floor Upload */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" component={'h3'} gutterBottom sx={{ mb: 1 }}>{getLoc('ui', 'uploadFloor')}</Typography>
              <Button
                component="label"
                role={undefined}
                variant={userFloorImage ? "contained" : "outlined"} // Indicate if custom floor is active
                tabIndex={-1}
                startIcon={<Upload />}
                color={userFloorImage ? "success" : "primary"}
                fullWidth
              >
                {userFloorImage ? "Custom Floor Applied" : "Upload Image"}
                <VisuallyHiddenInput type="file" accept="image/*" onChange={handleFloorUpload} />
              </Button>
               {userFloorImage && (
                 <Button
                   size="small"
                   onClick={() => {
                      setUserFloorImage(null);
                      setSelectedFloor(floorOptions[initialFloorIndex >= 0 && initialFloorIndex < floorOptions.length ? initialFloorIndex : 0]); // Revert to default or initial floor
                   }}
                   sx={{ mt: 1 }}
                   >
                   Clear Custom Floor
                 </Button>
               )}
            </Box>

          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
